import React, { useCallback, useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { reportsApi } from '../../services/mybudgetApi';
import { MonthComparison } from '../../types';
import { Card } from '../../components/shared/Card';
import { Loading } from '../../components/shared/Loading';
import { Select } from '../../components/shared/Select';
import { formatCurrency, MONTHS } from '../../constants';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function addMonths(year: number, month: number, offset: number) {
  const date = new Date(year, month - 1 + offset, 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

export function ComparePage() {
  const [monthsCount, setMonthsCount] = useState(6);
  const [data, setData] = useState<MonthComparison[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const now = new Date();
    setLoading(true);
    try {
      const months: { year: number; month: number }[] = [];
      for (let i = monthsCount - 1; i >= 0; i--) {
        months.push(addMonths(now.getFullYear(), now.getMonth() + 1, -i));
      }
      setData(await reportsApi.compare(months));
    } finally {
      setLoading(false);
    }
  }, [monthsCount]);

  useEffect(() => {
    load();
  }, [load]);

  const chartData = {
    labels: data.map((m) => `${MONTHS[m.month - 1].slice(0, 3)}/${String(m.year).slice(2)}`),
    datasets: [
      {
        label: 'Receitas',
        data: data.map((m) => m.totalIncome),
        backgroundColor: 'rgba(52, 211, 153, 0.75)',
        borderRadius: 6,
      },
      {
        label: 'Despesas',
        data: data.map((m) => m.totalExpenses),
        backgroundColor: 'rgba(248, 113, 113, 0.65)',
        borderRadius: 6,
      },
      {
        label: 'Saldo',
        data: data.map((m) => m.balance),
        backgroundColor: 'rgba(56, 189, 248, 0.5)',
        borderRadius: 6,
      },
    ],
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-100">Comparar Meses</h2>
          <p className="text-sm text-gray-400">
            Analise a evolução das suas finanças ao longo do tempo
          </p>
        </div>
        <Select
          label="Período"
          options={[
            { value: 3, label: 'Últimos 3 meses' },
            { value: 6, label: 'Últimos 6 meses' },
            { value: 12, label: 'Últimos 12 meses' },
          ]}
          value={monthsCount}
          onChange={(e) => setMonthsCount(Number(e.target.value))}
        />
      </div>

      {loading ? (
        <Loading />
      ) : (
        <>
          <Card title="Receitas vs Despesas vs Saldo">
            <Bar
              data={chartData}
              options={{
                responsive: true,
                plugins: { legend: { labels: { color: '#9ca3af' } } },
                scales: {
                  y: {
                    ticks: { color: '#9ca3af' },
                    grid: { color: '#243044' },
                  },
                  x: {
                    ticks: { color: '#9ca3af' },
                    grid: { color: '#243044' },
                  },
                },
              }}
            />
          </Card>

          <Card title="Tabela comparativa">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-dark-border text-left text-gray-400">
                    <th className="pb-3 pr-4">Mês</th>
                    <th className="pb-3 pr-4 text-right">Receitas</th>
                    <th className="pb-3 pr-4 text-right">Despesas</th>
                    <th className="pb-3 text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((m) => (
                    <tr key={`${m.year}-${m.month}`} className="border-b border-dark-border/50">
                      <td className="py-3 pr-4 text-gray-100">
                        {MONTHS[m.month - 1]} {m.year}
                      </td>
                      <td className="py-3 pr-4 text-right text-income">
                        {formatCurrency(m.totalIncome)}
                      </td>
                      <td className="py-3 pr-4 text-right text-expense">
                        {formatCurrency(m.totalExpenses)}
                      </td>
                      <td
                        className={`py-3 text-right font-medium ${m.balance >= 0 ? 'text-income' : 'text-expense'}`}
                      >
                        {formatCurrency(m.balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
